'use client';

import {
  Box,
  Button,
  Container,
  HStack,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import customerService from '@/_service/customer.service';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/navigation';

import Header from '@/app/[locale]/(home)/_components/Header'
import Breadcrumbs from '@/_components/Breadcrumbs';
import NumberToPrice from '@/_components/NumberToPrice';
import QRModal from '../../QRModal';
import { QRCodeCanvas } from 'qrcode.react';
import EditUserData from '../../EditBottomSheet';
import Addresses from './_components/Addresses';
import LogoutIcon from '@/_assets/icon-components/Logout';
import { authActions } from '@/_store/auth/auth.slice';
import MyCardsList from './_components/Cards';
import { formatPhoneNumber } from '@/_utils/formatPhoneNumber';
import { TrashIcon } from '@/_assets/icon-components';
import ConfirmModal from '@/_components/ConfirmModal';
import { checkoutActions } from '@/_store/checkout/checkout.slice';
import { getPublicSeoOrigin } from '@/i18n/urls'

const Desktop = () => {
  const { user } = useSelector((state: IRedux) => state.auth);
  const settings = useSelector((state: IRedux) => state.settings);
  const t = useTranslations();
  const router = useRouter();
  const dispatch = useDispatch();

  const [balance, setBalance] = useState<{ balance: number; deposit: number }>({
    balance: 0,
    deposit: 0,
  });
  const [isQR, setQR] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const isCashbackQRVisible =
    user?.phone &&
    settings?.data?.data?.cashback_display_places?.includes('profile');
  const isDeposit = settings?.data?.data?.is_deposit;

  const formattedPhoneNumber = formatPhoneNumber(user?.phone || '');

  const onLogout = () => {
    dispatch(authActions.logout());
    dispatch(checkoutActions.clear());
    router.push('/');
  };

  const onProfileDelete = () => {
    if (!user?.id) {
      return;
    }
    customerService.deleteProfile(user.id).then(() => {
      onLogout();
    });
  };

  useEffect(() => {
    if (!(balance?.balance || balance?.deposit) && user?.phone) {
      customerService
        .getIikoCustomerInfo({ phone: user.phone })
        .then((res) => {
          setBalance({
            balance: res?.data?.balance || 0,
            deposit: +res?.data?.deposit || 0,
          });
        })
        .catch((err) => console.log(err));
    }
  }, [user?.phone, balance?.balance, balance?.deposit]);

  const breadCrumbs = useMemo(
    () => [{ title: t('home'), link: '/' }, { title: t('profile') }],
    [t]
  );

  const domain = getPublicSeoOrigin();

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadCrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title,
      item: item.link ? `${domain}${item.link}` : undefined,
    })),
  };

  return (
    <Box mb={'84px'}>
      <Header />
      <Container maxW={'1200px'} p={0}>
        <Breadcrumbs data={breadCrumbs} />
        <Text as={'h1'} fontWeight={700} fontSize={24}>
          {t('profile')}
        </Text>
        <SimpleGrid
          w='full'
          gridTemplateColumns='580px 1fr'
          gap={8}
          alignItems='start'
        >
          <Stack maxW='580px' w='full'>
            <Stack bg='background.card' rounded={12} p={4} mt={4} w='full'>
              <HStack justifyContent='space-between'>
                <Text fontSize={20} fontWeight={700}>
                  {user?.name}
                </Text>
                <Button
                  size='lg'
                  rounded={12}
                  variant='subtle'
                  onClick={() => setIsOpenModal(true)}
                >
                  {t('change')}
                </Button>
              </HStack>
              <HStack w='full' justifyContent='space-between' alignItems='end'>
                <SimpleGrid templateColumns='1fr 1fr' gap={6}>
                  <div>
                    <Text color='text.muted' mb={1} fontSize='sm'>
                      {t('phone_number')}
                    </Text>
                    <Text>{formattedPhoneNumber}</Text>
                  </div>
                  <div>
                    <Text color='text.muted' mb={1} fontSize='sm'>
                      {t('birthday')}
                    </Text>
                    <Text>{user?.date_of_birth || '-'}</Text>
                  </div>
                  {isCashbackQRVisible && (
                    <div>
                      <Text fontSize='sm' color='gray.600'>
                        {t('bonus_points')}
                      </Text>
                      <NumberToPrice value={balance?.balance} />
                    </div>
                  )}
                  {isDeposit && (
                    <div>
                      <Text fontSize='sm' color='text.muted'>
                        {t('deposit_points')}
                      </Text>
                      <NumberToPrice value={balance?.deposit} />
                    </div>
                  )}
                </SimpleGrid>
                {isCashbackQRVisible && (
                  <Box px={3} onClick={() => setQR(true)} cursor='pointer'>
                    <QRCodeCanvas
                      value={user?.phone?.replace('+', '') || ''}
                      size={24}
                      bgColor='#ffffff'
                      fgColor='#000000'
                      level='M'
                    />
                  </Box>
                )}
              </HStack>
            </Stack>
            <Stack bg='background.card' rounded={12} p={4} mt={8} gap={6}>
              <Text fontSize={20} fontWeight={700}>
                {t('addresses')}
              </Text>
              <Addresses />
            </Stack>
            <Button
              mt={8}
              w='full'
              size='lg'
              rounded={16}
              color='red'
              h='56px'
              variant='outline'
              colorPalette='red'
              onClick={onLogout}
              borderColor='red'
            >
              <LogoutIcon />
              {t('logout')}
            </Button>
            {!!user?.id && (
              <Button
                mt={4}
                w='full'
                size='lg'
                rounded={16}
                color='white'
                h='56px'
                variant='solid'
                colorPalette='red'
                onClick={() => setIsDeleteDialogOpen(true)}
                borderColor='red'
              >
                <TrashIcon color='white' />
                {t('delete_profile')}
              </Button>
            )}
          </Stack>

          <MyCardsList />
        </SimpleGrid>
      </Container>
      <ConfirmModal
        title={t('delete_profile')}
        isWarning
        description={t('sure_delete_profile')}
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => onProfileDelete()}
        text={{ confirm: 'delete', cancel: 'cancel' }}
      />
      <QRModal open={isQR} onOpenChange={(e) => setQR(e)} />
      <EditUserData setIsOpen={setIsOpenModal} isOpen={isOpenModal} />

      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
    </Box>
  );
};

export default Desktop;
